import type {
  ClipboardEvent,
  KeyboardEvent,
  ReactNode,
  RefObject,
} from "react";
import { useEffect, useRef, useState } from "react";

import {
  COMPOSER_ATTACH_ACTION_LABEL,
  COMPOSER_CAPTION_PLACEHOLDER,
  COMPOSER_EMOJI_ACTION_LABEL,
  COMPOSER_GIF_ACTION_LABEL,
  COMPOSER_INPUT_LABEL,
  COMPOSER_INPUT_PLACEHOLDER,
  COMPOSER_PHOTO_RETRY_LABEL,
  COMPOSER_PHOTO_UPLOAD_FAILED_LABEL,
  COMPOSER_POLL_ACTION_LABEL,
  COMPOSER_REMOVE_PHOTO_LABEL,
  COMPOSER_TOO_MANY_PHOTOS_MESSAGE,
} from "./chatExtrasCopy";
import { ComposerAttachDialog } from "./ComposerAttachDialog";
import { ComposerPdfChip } from "./ComposerPdfChip";
import { imageFilesFrom, insertAtSelection } from "./composerEmojiLogic";
import { CHAT_PDF_CONTENT_TYPE } from "./composerPdfLogic";
import { EmojiPickerSheet } from "./EmojiPickerSheet";
import { GamePickerSheet } from "./GamePickerSheet";
import { GifPickerSheet } from "./GifPickerSheet";
import {
  MentionSuggestionList,
  type typeMentionMember,
} from "./MentionSuggestionList";
import { buildMentionToken } from "./messageText";
import type { typePollDraft } from "./pollComposeLogic";
import { PollComposeSheet } from "./PollComposeSheet";
import { CHAT_PHOTO_CONTENT_TYPES } from "./useChatPhotoUpload";
import { useComposerPdfUpload } from "./useComposerPdfUpload";
import { useComposerPhotoUpload } from "./useComposerPhotoUpload";
import { isGifActionDisabled, useGifAvailability } from "./useGifAvailability";

export const MESSAGE_BODY_MAX_CODE_POINTS = 5000;

/** ISSUE-1057. A paperclip: the one Attach action opens the photo or PDF
 *  chooser. Same `h-icon-md w-icon-md` box as the photo mark it replaced. */
function AttachIcon(): ReactNode {
  return (
    <svg
      className="h-icon-md w-icon-md"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M16 9.5 10.25 15.25a3.75 3.75 0 0 1-5.3-5.3l6-6a2.5 2.5 0 0 1 3.54 3.54l-6 6a1.25 1.25 0 0 1-1.77-1.77L12.5 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PollIcon(): ReactNode {
  return (
    <svg
      className="h-icon-md w-icon-md"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3" y="10" width="3" height="7" rx="0.5" fill="currentColor" />
      <rect x="8.5" y="6" width="3" height="11" rx="0.5" fill="currentColor" />
      <rect x="14" y="3" width="3" height="14" rx="0.5" fill="currentColor" />
    </svg>
  );
}

/** The composer's GIF action mark. Letterforms rather than a pictogram:
 *  "GIF" is the only word a fan reliably recognises for this control, and R4
 *  needs the same mark legible in its dimmed disabled state. */
function GifIcon(): ReactNode {
  return (
    <svg
      className="h-icon-md w-icon-md"
      viewBox="0 0 24 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="1.5"
        y="3"
        width="21"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M9 8.5H6.75v3H9v-1.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.75 8v4M14.75 12V8h2.75M14.75 10.25h2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** ISSUE-1366. A plain smiley outline, the same 20px box as its neighbors. */
function EmojiIcon(): ReactNode {
  return (
    <svg
      className="h-icon-md w-icon-md"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="10"
        cy="10"
        r="7.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="7.5" cy="8.5" r="1" fill="currentColor" />
      <circle cx="12.5" cy="8.5" r="1" fill="currentColor" />
      <path
        d="M7 11.75c.75 1.25 1.75 1.75 3 1.75s2.25-.5 3-1.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function draftKey(chatId: string): string {
  return `sm.messages.draft.${chatId}`;
}

function findMentionQuery(text: string, cursor: number): string | null {
  const upToCursor = text.slice(0, cursor);
  const match = /@(\S*)$/.exec(upToCursor);
  return match?.[1] ?? null;
}

/** ISSUE-1057 R4. What the composer hands up for one ready PDF. */
export type typeComposerPdfSend = {
  mediaObjectId: string;
  fileName: string;
  byteSize: number;
};

export type typeComposerProps = {
  chatId: string;
  members: typeMentionMember[];
  replyTo: { messageId: string; preview: string } | null;
  onCancelReply: () => void;
  editingMessage: { messageId: string; body: string } | null;
  onCancelEdit: () => void;
  onSend: (body: string, replyToMessageId: string | null) => void;
  /** R2. `caption` is the composer's own text field, trimmed to null when
   *  blank -- a photo message never requires a caption. */
  onSendPhotos: (
    mediaObjectIds: string[],
    caption: string | null,
    replyToMessageId: string | null,
  ) => void;
  /** R5. */
  onSendPoll: (draft: typePollDraft, replyToMessageId: string | null) => void;
  /** R3. Fires the instant a picker tile is tapped: the provider id goes
   *  straight to `/gif/select` and then `/message/send`, with no confirm
   *  step anywhere in between. */
  onSendGif: (providerGifId: string, replyToMessageId: string | null) => void;
  /** ISSUE-1057 R4. One PDF per message; messaging-api's pdf branch takes no caption. */
  onSendPdf: (
    pdf: typeComposerPdfSend,
    replyToMessageId: string | null,
  ) => void;
  onSaveEdit: (messageId: string, body: string) => void;
  isSubmitting: boolean;
  /**
   * ISSUE-1366. The composer writes its photo intake here, so the
   * conversation pane can hand it image files dropped anywhere on the pane.
   * Null while the composer cannot take photos (editing a message, or a PDF
   * is already staged).
   */
  photoIntakeRef?: RefObject<((files: File[]) => void) | null>;
};

export const Composer = (props: typeComposerProps): ReactNode => {
  const [text, setText] = useState("");
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [preEditDraft, setPreEditDraft] = useState<string | null>(null);
  const [isPollSheetOpen, setIsPollSheetOpen] = useState(false);
  const [isGifSheetOpen, setIsGifSheetOpen] = useState(false);
  const [isAttachDialogOpen, setIsAttachDialogOpen] = useState(false);
  // ISSUE-1365. The game picker opened from the Attach chooser.
  const [isGamePickerOpen, setIsGamePickerOpen] = useState(false);
  const [isEmojiSheetOpen, setIsEmojiSheetOpen] = useState(false);
  // ISSUE-1366. Where an emoji lands. Saved when the picker opens, because
  // the textarea loses focus (and in some browsers its selection) to the
  // sheet, then moved past each inserted emoji so a run of taps reads in
  // order. The emoji button shows from 768px up only: under that the row
  // has no room left for the text field, and a phone's own keyboard already
  // has an emoji key.
  const emojiSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const wasEditingRef = useRef(false);
  const photos = useComposerPhotoUpload();
  const pdf = useComposerPdfUpload();
  const gifAvailability = useGifAvailability();
  // R4: disabled, never hidden. A fan who sees a dimmed control knows the
  // feature exists and is not working; one whose control vanished is left
  // wondering where it went.
  const isGifDisabled = isGifActionDisabled(gifAvailability);

  // The unsent draft, mirrored out of state so the save below can read it
  // while the composer unmounts. It cannot read the textarea: on unmount
  // React detaches host refs before passive cleanups run, so the field is
  // already gone and the draft would read as blank and be deleted. That is
  // exactly what happens when the messages screen remounts the pane for a
  // different chat, or the fan leaves /messages. While a message is being
  // edited the field holds that message's body, so the draft is the text
  // that preceded the edit.
  const draftValueRef = useRef("");
  const draftValue =
    props.editingMessage !== null ? (preEditDraft ?? "") : text;
  useEffect(() => {
    draftValueRef.current = draftValue;
  }, [draftValue]);

  // Draft lifecycle (S11): load on mount / chatId change, save on unmount.
  useEffect(() => {
    const saved = localStorage.getItem(draftKey(props.chatId));
    setText(saved ?? "");

    return () => {
      const current = draftValueRef.current;
      if (current.trim().length === 0) {
        localStorage.removeItem(draftKey(props.chatId));
      } else {
        localStorage.setItem(draftKey(props.chatId), current);
      }
    };
  }, [props.chatId]);

  // Entering/leaving edit mode swaps the field's content without touching
  // the draft store; canceling restores whatever draft text preceded it.
  useEffect(() => {
    const isEditingNow = props.editingMessage !== null;
    if (
      isEditingNow &&
      !wasEditingRef.current &&
      props.editingMessage !== null
    ) {
      setPreEditDraft(text);
      setText(props.editingMessage.body);
    } else if (!isEditingNow && wasEditingRef.current) {
      setText(preEditDraft ?? "");
      setPreEditDraft(null);
    }
    wasEditingRef.current = isEditingNow;
  }, [props.editingMessage]);

  const codePointCount = Array.from(text).length;
  const isOverCap = codePointCount > MESSAGE_BODY_MAX_CODE_POINTS;
  const showCounter = codePointCount > MESSAGE_BODY_MAX_CODE_POINTS - 200;

  const readyPhotoMediaIds = photos.attachments.flatMap((a) =>
    a.state === "ready" && a.mediaId !== null ? [a.mediaId] : [],
  );
  const hasUploadingPhotos = photos.attachments.some(
    (a) => a.state === "uploading",
  );
  const hasStagedPhotos = photos.attachments.length > 0;
  const hasStagedPdf = pdf.attachment !== null;
  const readyPdf =
    pdf.attachment !== null &&
    pdf.attachment.state === "ready" &&
    pdf.attachment.mediaId !== null
      ? {
          mediaObjectId: pdf.attachment.mediaId,
          fileName: pdf.attachment.file.name,
          byteSize: pdf.attachment.file.size,
        }
      : null;

  // Content-based, not just text: at least one ready photo also satisfies
  // MSG-12's "only enabled with content", with or without a caption. While
  // any photo is still uploading, Send waits rather than sending a partial
  // set (R2). A staged PDF gates Send the same way (ISSUE-1057 R3): Send
  // stays off until it is ready, and a failed PDF keeps it off until the fan
  // retries or removes it.
  const isSendDisabled = props.isSubmitting
    ? true
    : props.editingMessage !== null
      ? text.trim().length === 0 || isOverCap
      : hasStagedPdf
        ? readyPdf === null
        : hasStagedPhotos
          ? readyPhotoMediaIds.length === 0 || hasUploadingPhotos
          : text.trim().length === 0 || isOverCap;

  const filteredMembers =
    mentionQuery === null
      ? []
      : props.members.filter((member) =>
          member.displayName
            .toLowerCase()
            .startsWith(mentionQuery.toLowerCase()),
        );

  // ISSUE-1366. Photos are refused while editing (an edit is text only) and
  // while a PDF is staged (a PDF goes alone), the same two cases where the
  // Attach dialog disables its photo choice.
  const canTakePhotos = props.editingMessage === null && !hasStagedPdf;
  const addPhotoFiles = (files: File[]): boolean => {
    if (!canTakePhotos) return false;
    const images = imageFilesFrom(files);
    if (images.length === 0) return false;
    photos.addFiles(images);
    return true;
  };
  // Re-pointed after every render, since `addPhotoFiles` closes over this
  // render's state, and cleared on unmount so a drop never reaches a
  // composer that is gone.
  const photoIntakeRef = props.photoIntakeRef;
  useEffect(() => {
    if (photoIntakeRef === undefined) return undefined;
    photoIntakeRef.current = canTakePhotos
      ? (files) => {
          addPhotoFiles(files);
        }
      : null;
    return () => {
      photoIntakeRef.current = null;
    };
  });

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>): void => {
    // A screenshot or a copied image arrives as clipboard files. Plain text
    // pastes carry none, so they fall through to the browser untouched.
    if (addPhotoFiles(Array.from(e.clipboardData.files))) {
      e.preventDefault();
    }
  };

  const openEmojiSheet = (): void => {
    const field = textareaRef.current;
    emojiSelectionRef.current = {
      start: field?.selectionStart ?? text.length,
      end: field?.selectionEnd ?? text.length,
    };
    setIsEmojiSheetOpen(true);
  };

  const handleSelectEmoji = (emoji: string): void => {
    const saved = emojiSelectionRef.current ?? {
      start: text.length,
      end: text.length,
    };
    const next = insertAtSelection({
      text,
      selectionStart: saved.start,
      selectionEnd: saved.end,
      insert: emoji,
    });
    emojiSelectionRef.current = { start: next.caret, end: next.caret };
    setText(next.text);
    setMentionQuery(null);
  };

  const handleEmojiSheetOpenChange = (open: boolean): void => {
    setIsEmojiSheetOpen(open);
    if (open) return;
    // Back to typing, with the caret right after the last emoji.
    const caret = emojiSelectionRef.current?.start ?? null;
    requestAnimationFrame(() => {
      const field = textareaRef.current;
      if (field === null) return;
      field.focus();
      if (caret !== null) field.setSelectionRange(caret, caret);
    });
  };

  const submit = (): void => {
    if (isSendDisabled) return;
    const body = text.trim();
    if (props.editingMessage !== null) {
      props.onSaveEdit(props.editingMessage.messageId, body);
      setText("");
      localStorage.removeItem(draftKey(props.chatId));
      return;
    }
    // ISSUE-1057. The PDF goes alone: messaging-api's pdf branch takes no
    // caption, so typed text stays in the field for the fan's next Send.
    if (readyPdf !== null) {
      props.onSendPdf(readyPdf, props.replyTo?.messageId ?? null);
      pdf.reset();
      return;
    }
    if (hasStagedPhotos) {
      props.onSendPhotos(
        readyPhotoMediaIds,
        body.length > 0 ? body : null,
        props.replyTo?.messageId ?? null,
      );
      photos.reset();
    } else {
      props.onSend(body, props.replyTo?.messageId ?? null);
    }
    setText("");
    localStorage.removeItem(draftKey(props.chatId));
  };

  const handleChange = (value: string): void => {
    setText(value);
    const cursor = textareaRef.current?.selectionStart ?? value.length;
    setMentionQuery(findMentionQuery(value, cursor));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handleSelectMention = (member: typeMentionMember): void => {
    const cursor = textareaRef.current?.selectionStart ?? text.length;
    const upToCursor = text.slice(0, cursor);
    const replaced = upToCursor.replace(
      /@\S*$/,
      `${buildMentionToken(member.displayName, member.userId)} `,
    );
    setText(replaced + text.slice(cursor));
    setMentionQuery(null);
  };

  return (
    <div className="flex flex-col gap-1">
      {mentionQuery !== null ? (
        <MentionSuggestionList
          members={filteredMembers}
          query={mentionQuery}
          onSelect={handleSelectMention}
        />
      ) : null}
      {props.replyTo !== null ? (
        <div className="flex items-center justify-between gap-2 bg-surface px-3 py-1">
          <span className="truncate text-caption text-ink-tertiary">
            Replying to: {props.replyTo.preview}
          </span>
          <button
            type="button"
            onClick={props.onCancelReply}
            aria-label="Cancel reply"
            className="min-h-touch-target min-w-touch-target text-body"
          >
            ✕
          </button>
        </div>
      ) : null}
      {props.editingMessage !== null ? (
        <div className="flex items-center justify-between gap-2 bg-surface px-3 py-1">
          <span className="text-caption text-ink-tertiary">
            Editing message
          </span>
          <button
            type="button"
            onClick={props.onCancelEdit}
            className="text-body text-primary"
          >
            Cancel
          </button>
        </div>
      ) : null}
      {hasStagedPhotos ? (
        <div
          className="flex gap-2 overflow-x-auto px-1"
          data-testid="composer-photo-attachments"
        >
          {photos.attachments.map((attachment) => (
            <div
              key={attachment.localId}
              data-testid="composer-photo-attachment"
              className="relative h-16 w-16 shrink-0 overflow-hidden rounded-sm border border-hairline"
            >
              <img
                src={attachment.previewUrl}
                alt=""
                className="h-full w-full object-cover"
              />
              {attachment.state === "uploading" ? (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-scrim"
                  data-testid="composer-photo-attachment-uploading"
                >
                  <span className="text-caption text-on-primary">
                    Uploading
                  </span>
                </div>
              ) : null}
              {attachment.state === "failed" ? (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-scrim text-center"
                  data-testid="composer-photo-attachment-failed"
                >
                  <span className="text-caption text-on-primary">
                    {COMPOSER_PHOTO_UPLOAD_FAILED_LABEL}
                  </span>
                  <button
                    type="button"
                    data-testid="composer-photo-attachment-retry"
                    onClick={() => {
                      photos.retryAttachment(attachment.localId);
                    }}
                    className="text-caption text-on-primary underline"
                  >
                    {COMPOSER_PHOTO_RETRY_LABEL}
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                aria-label={COMPOSER_REMOVE_PHOTO_LABEL}
                data-testid="composer-photo-attachment-remove"
                onClick={() => {
                  photos.removeAttachment(attachment.localId);
                }}
                className="absolute top-0 right-0 flex h-5 w-5 items-center justify-center bg-scrim text-caption text-on-primary"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {pdf.attachment !== null ? (
        <ComposerPdfChip
          attachment={pdf.attachment}
          onRetry={pdf.retry}
          onRemove={pdf.remove}
        />
      ) : null}
      {photos.hasTooManyRejection ? (
        <p
          className="px-1 text-caption text-error"
          data-testid="composer-photo-too-many"
        >
          {COMPOSER_TOO_MANY_PHOTOS_MESSAGE}
        </p>
      ) : null}
      <div className="flex items-center gap-2">
        {props.editingMessage === null ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={CHAT_PHOTO_CONTENT_TYPES.join(",")}
              multiple
              className="hidden"
              data-testid="composer-photo-input"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length > 0) photos.addFiles(files);
                e.target.value = "";
              }}
            />
            <input
              ref={pdfInputRef}
              type="file"
              accept={CHAT_PDF_CONTENT_TYPE}
              className="hidden"
              data-testid="composer-pdf-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) pdf.addFile(file);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              aria-label={COMPOSER_ATTACH_ACTION_LABEL}
              aria-haspopup="dialog"
              data-testid="composer-attach-action"
              onClick={() => {
                setIsAttachDialogOpen(true);
              }}
              className="flex min-h-touch-target min-w-touch-target items-center justify-center text-ink-secondary"
            >
              <AttachIcon />
            </button>
            <button
              type="button"
              aria-label={COMPOSER_POLL_ACTION_LABEL}
              data-testid="composer-poll-action"
              onClick={() => {
                setIsPollSheetOpen(true);
              }}
              className="flex min-h-touch-target min-w-touch-target items-center justify-center text-ink-secondary"
            >
              <PollIcon />
            </button>
            <button
              type="button"
              aria-label={COMPOSER_GIF_ACTION_LABEL}
              data-testid="composer-gif-action"
              disabled={isGifDisabled}
              onClick={() => {
                setIsGifSheetOpen(true);
              }}
              className="flex min-h-touch-target min-w-touch-target items-center justify-center text-ink-secondary disabled:opacity-40"
            >
              <GifIcon />
            </button>
          </>
        ) : null}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            handleChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          rows={1}
          aria-label={COMPOSER_INPUT_LABEL}
          placeholder={
            hasStagedPhotos && props.editingMessage === null
              ? COMPOSER_CAPTION_PLACEHOLDER
              : COMPOSER_INPUT_PLACEHOLDER
          }
          data-testid="composer-input"
          className="min-h-touch-target flex-1 rounded-md border border-hairline px-3 py-2 text-body placeholder:text-ink-tertiary"
        />
        <button
          type="button"
          aria-label={COMPOSER_EMOJI_ACTION_LABEL}
          aria-haspopup="dialog"
          data-testid="composer-emoji-action"
          onClick={openEmojiSheet}
          className="hidden min-h-touch-target min-w-touch-target shrink-0 items-center justify-center text-ink-secondary md:flex"
        >
          <EmojiIcon />
        </button>
        <button
          type="button"
          disabled={isSendDisabled}
          onClick={submit}
          className="min-h-touch-target rounded-md bg-primary-solid px-4 text-body text-on-primary disabled:opacity-40"
        >
          {props.editingMessage !== null ? "Save" : "Send"}
        </button>
      </div>
      {showCounter ? (
        <span
          className={`text-caption ${isOverCap ? "text-error" : "text-ink-tertiary"}`}
        >
          {codePointCount} / {MESSAGE_BODY_MAX_CODE_POINTS}
        </span>
      ) : null}
      <ComposerAttachDialog
        open={isAttachDialogOpen}
        onOpenChange={setIsAttachDialogOpen}
        isPhotoDisabled={hasStagedPdf}
        isPdfDisabled={hasStagedPdf || hasStagedPhotos}
        onPickPhoto={() => {
          fileInputRef.current?.click();
        }}
        onPickPdf={() => {
          pdfInputRef.current?.click();
        }}
        onPickGame={() => {
          setIsGamePickerOpen(true);
        }}
      />
      <GamePickerSheet
        chatId={props.chatId}
        open={isGamePickerOpen}
        onOpenChange={setIsGamePickerOpen}
      />
      <EmojiPickerSheet
        open={isEmojiSheetOpen}
        onOpenChange={handleEmojiSheetOpenChange}
        onSelect={handleSelectEmoji}
      />
      <GifPickerSheet
        open={isGifSheetOpen}
        onOpenChange={setIsGifSheetOpen}
        onSelect={(providerGifId) => {
          props.onSendGif(providerGifId, props.replyTo?.messageId ?? null);
        }}
      />
      <PollComposeSheet
        open={isPollSheetOpen}
        onOpenChange={setIsPollSheetOpen}
        isSubmitting={props.isSubmitting}
        onSubmit={(draft) => {
          props.onSendPoll(draft, props.replyTo?.messageId ?? null);
        }}
      />
    </div>
  );
};
